import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';

export type GaugeSize = 'sm' | 'md' | 'lg';

/**
 * Gauge chart displaying a value against a maximum using a semi-circular arc.
 *
 * @example
 *   ```html
 *   <hx-gauge-chart [value]="83" [maxValue]="100"></hx-gauge-chart>
 *   ```;
 */
@Component({
  selector: 'hx-gauge-chart',
  standalone: true,
  imports: [NgClass],
  templateUrl: './gauge-chart.html',
  styleUrls: ['./gauge-chart.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxGaugeChart {
  private static nextId = 0;
  /** Unique gradient ID for this gauge instance. */
  readonly gradientId = `gaugeGradient${HxGaugeChart.nextId++}`;

  /** Value to render on the gauge. */
  readonly value = input.required<number>();
  /** Maximum value used to compute the score percentage. @default 100 */
  readonly maxValue = input<number>(100);
  /** Visual size of the gauge. @default 'md' */
  readonly size = input<GaugeSize>('md');
  /** Background arc stroke color. @default '#0F0F0F0A' */
  readonly backgroundStroke = input<string>('#0F0F0F0A');
  /** Filled arc stroke color. @default '#ABC37D' */
  readonly scoreStroke = input<string>('#ABC37D');

  private readonly percentage = computed(() => {
    const val = this.value();
    const max = this.maxValue();
    return max > 0 ? (val / max) * 100 : 0;
  });

  protected readonly dashArray = computed(() => {
    return `${this.percentage()} 100`;
  });

  protected readonly containerClass = computed(() => {
    const sizeMap = {
      sm: 'h-[8rem] w-[8rem]',
      md: 'h-[15.625rem] w-[15.625rem]',
      lg: 'h-[14rem] w-[14rem]',
    };

    return `box-border block overflow-hidden rounded-[0.125rem] ${sizeMap[this.size()]}`;
  });

  protected readonly valueFontSize = computed(() => {
    const sizeMap = { sm: 28, md: 30, lg: 44 };
    return sizeMap[this.size()];
  });

  protected readonly labelFontSize = computed(() => {
    const sizeMap = { sm: 11, md: 11, lg: 15 };
    return sizeMap[this.size()];
  });
}
